// Short-lived visual effects in world space: floating glyphs / sparkles (one Points draw call)
// and connector pulses (a light beam rising from a pod plus a ripple on its top).
import {
  BufferAttribute, BufferGeometry, Color, DynamicDrawUsage, Mesh, MeshBasicMaterial, Points, Sprite,
  SpriteMaterial,
} from 'three';
import { ease } from './anim.js';
import { additive } from './materials.js';

const _c = new Color();

export class Particles {
  /**
   * @param {object} mats material library (uses mats.glyphs)
   * @param {number} [max=384]
   */
  constructor(mats, max = 384) {
    this.max = max;
    this.count = 0;
    this.p = [];
    const geo = new BufferGeometry();
    this.pos = new Float32Array(max * 3);
    this.life = new Float32Array(max);
    this.glyph = new Float32Array(max);
    this.color = new Float32Array(max * 3);
    this.size = new Float32Array(max);
    const attr = (arr, n) => {
      const a = new BufferAttribute(arr, n);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    geo.setAttribute('position', attr(this.pos, 3));
    geo.setAttribute('aLife', attr(this.life, 1));
    geo.setAttribute('aGlyph', attr(this.glyph, 1));
    geo.setAttribute('aColor', attr(this.color, 3));
    geo.setAttribute('aSize', attr(this.size, 1));
    geo.setDrawRange(0, 0);
    this.geometry = geo;
    this.points = new Points(geo, mats.glyphs);
    this.points.frustumCulled = false;
    this.points.renderOrder = 30;
  }

  /**
   * @param {{x:number,y:number,z:number,vx?:number,vy?:number,vz?:number,life?:number,glyph?:number,
   *   color:string, size?:number, drag?:number, gravity?:number}} o
   */
  emit(o) {
    if (this.p.length >= this.max) this.p.shift();
    this.p.push({
      x: o.x, y: o.y, z: o.z, vx: o.vx || 0, vy: o.vy ?? 0.5, vz: o.vz || 0, age: 0,
      life: o.life || 1.6, glyph: o.glyph || 0, color: _c.set(o.color).toArray(), size: o.size || 0.3,
      drag: o.drag ?? 0.6, gravity: o.gravity || 0,
    });
  }

  get active() {
    return this.p.length > 0;
  }

  update(dt) {
    const alive = [];
    for (const q of this.p) {
      q.age += dt;
      if (q.age >= q.life) continue;
      const k = Math.exp(-q.drag * dt);
      q.vx *= k; q.vz *= k;
      q.vy = q.vy * k - q.gravity * dt;
      q.x += q.vx * dt; q.y += q.vy * dt; q.z += q.vz * dt;
      alive.push(q);
    }
    this.p = alive;
    alive.forEach((q, i) => {
      this.pos[i * 3] = q.x; this.pos[i * 3 + 1] = q.y; this.pos[i * 3 + 2] = q.z;
      this.life[i] = q.age / q.life;
      this.glyph[i] = q.glyph;
      this.color[i * 3] = q.color[0]; this.color[i * 3 + 1] = q.color[1]; this.color[i * 3 + 2] = q.color[2];
      this.size[i] = q.size;
    });
    const g = this.geometry;
    for (const name of ['position', 'aLife', 'aGlyph', 'aColor', 'aSize']) g.attributes[name].needsUpdate = true;
    g.setDrawRange(0, alive.length);
    return alive.length > 0;
  }

  clear() {
    this.p = [];
    this.geometry.setDrawRange(0, 0);
  }

  dispose() {
    this.geometry.dispose();
  }
}

/** Connector pulses: a beam of light rising from a pod top and a ripple spreading on it. */
export class Pulses {
  constructor(parent, geos, mats) {
    this.parent = parent;
    this.geos = geos;
    this.mats = mats;
    this.list = [];
  }

  get active() {
    return this.list.length > 0;
  }

  /**
   * @param {{x:number,y:number,z:number}} at world position of the pod top centre
   * @param {string} color hex
   * @param {boolean} reducedMotion
   * @param {boolean} [glow] additive light (night); plain translucent colour otherwise
   */
  spawn(at, color, reducedMotion, glow = false) {
    const fx = (m) => (glow ? additive(m) : m);
    const beamMat = fx(new MeshBasicMaterial({
      color, map: this.mats.tx.beamFade, transparent: true, depthWrite: false, toneMapped: false, opacity: 0,
    }));
    const beam = new Mesh(this.geos.beam, beamMat);
    beam.position.set(at.x, at.y, at.z);
    beam.renderOrder = 6;
    const ringMat = fx(new MeshBasicMaterial({
      color, transparent: true, depthWrite: false, toneMapped: false, opacity: 0,
    }));
    const ring = new Mesh(this.geos.ring, ringMat);
    ring.position.set(at.x, at.y + 0.02, at.z);
    ring.renderOrder = 6;
    const orbMat = fx(new SpriteMaterial({
      map: this.mats.tx.halo, color, transparent: true, depthWrite: false, toneMapped: false, opacity: 0,
    }));
    const orb = new Sprite(orbMat);
    orb.position.set(at.x, at.y + 0.3, at.z);
    orb.renderOrder = 7;
    this.parent.add(beam, ring, orb);
    this.list.push({ beam, ring, orb, at: { ...at }, age: 0, duration: reducedMotion ? 0.9 : 1.4, rm: reducedMotion });
  }

  update(dt) {
    const keep = [];
    for (const p of this.list) {
      p.age += dt;
      const t = Math.min(1, p.age / p.duration);
      if (p.rm) {
        const a = Math.sin(Math.PI * t);
        p.beam.scale.set(1.4, 5, 1.4);
        p.beam.material.opacity = 0.55 * a;
        p.ring.scale.setScalar(1.6);
        p.ring.material.opacity = 0.4 * a;
        p.orb.material.opacity = 0;
      } else {
        const rise = ease.outCubic(Math.min(1, t / 0.45));
        const fade = t < 0.45 ? 1 : 1 - ease.inOutSine((t - 0.45) / 0.55);
        p.beam.scale.set(1 + 0.6 * (1 - fade), 0.2 + 7.5 * rise, 1 + 0.6 * (1 - fade));
        p.beam.material.opacity = 0.7 * fade * Math.min(1, t * 8);
        const rr = ease.outCubic(t);
        p.ring.scale.setScalar(0.4 + 2.4 * rr);
        p.ring.material.opacity = 0.7 * (1 - rr);
        p.orb.position.y = p.at.y + 0.3 + 8 * ease.inOutSine(Math.min(1, t / 0.7));
        p.orb.scale.setScalar(0.9);
        p.orb.material.opacity = t < 0.7 ? Math.min(1, t * 6) : Math.max(0, 1 - (t - 0.7) / 0.3);
      }
      if (t >= 1) {
        this.parent.remove(p.beam, p.ring, p.orb);
        p.beam.material.dispose();
        p.ring.material.dispose();
        p.orb.material.dispose();
      } else {
        keep.push(p);
      }
    }
    this.list = keep;
    return keep.length > 0;
  }

  clear() {
    for (const p of this.list) p.age = 1e9;
    this.update(0);
  }
}
